// src/views/Representatives/ManageFunders.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardTitle,
  Table,
  Alert,
  Spinner,
  Badge,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  Label,
  FormGroup
} from 'reactstrap';
import axios from '../../api/Axios';
import usePermissions from '../../hooks/usePermissions';
import { CURRENCIES } from '../../constants/currencies';

const ManageFunders = () => {
  console.log('🚀 ManageFunders component is loading...');
  const [funders, setFunders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFunder, setSelectedFunder] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [representatives, setRepresentatives] = useState({
    primary_representatives: [],
    secondary_representatives: [],
    officers: []
  });
  const [representativesLoading, setRepresentativesLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedFunder, setEditedFunder] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const navigate = useNavigate();
  
  // Get permissions to check if user can add funders
  const { 
    canCreateFunders, 
    canManageFunders,
    canManageFunderRepresentatives, 
    canManageFunderOfficers,
    hasAnyPermission
  } = usePermissions();

  // Unified permission checks - if user can manage funders, they can manage representatives and officers
  const canViewFunderRepresentatives = useCallback(() => hasAnyPermission([
    'view_funder', 'add_funder', 'change_funder'
  ]), [hasAnyPermission]);
  
  const canViewFunderOfficers = useCallback(() => hasAnyPermission([
    'view_funder', 'add_funder', 'change_funder'
  ]), [hasAnyPermission]);

  // Handle tab visibility based on permissions
  useEffect(() => {
    // If current active tab is not available due to permissions, switch to details
    if (activeTab === 'representatives' && !canViewFunderRepresentatives()) {
      setActiveTab('details');
    } else if (activeTab === 'officers' && !canViewFunderOfficers()) {
      setActiveTab('details');
    }
  }, [activeTab, canViewFunderRepresentatives, canViewFunderOfficers]);

  const fetchAllFunders = useCallback(async () => {
    try {
      console.log('Fetching all funders (fallback)...');
      const response = await axios.get('accounts/funders/list/');
      console.log('All Funders API Response:', response.data);
      
      // Debug individual funders - show all fields
      response.data.forEach((funder, index) => {
        console.log(`All Funder ${index} - All fields:`, funder);
      });
      
      setFunders(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching all funders:', error);
      setError('Failed to load funders list. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserAssignedFunders = useCallback(async (userId) => {
    try {
      console.log('Fetching user assigned funds for user:', userId);
      const response = await axios.get(`/api/accounts/user-fund-assignments/${userId}/assigned_funds/`);
      console.log('User assigned funds response:', response.data);
      
      if (response.data.assigned_funds && response.data.assigned_funds.length > 0) {
        // Debug individual funders - show all fields
        response.data.assigned_funds.forEach((funder, index) => {
          console.log(`Assigned Funder ${index} - All fields:`, funder);
        });
        
        setFunders(response.data.assigned_funds);
        setError('');
        console.log(`Loaded ${response.data.assigned_funds.length} assigned funds for user`);
      } else {
        console.log('No assigned funds found for user');
        setFunders([]);
        setError('All funds in your BMO have been restricted from your account. Please contact your BMO administrator to remove fund restrictions.');
      }
    } catch (error) {
      console.error('Error fetching user assigned funds:', error);
      // Fallback to all funders if assigned funds fetch fails
      console.log('Falling back to all funders due to error');
      await fetchAllFunders();
    } finally {
      setLoading(false);
    }
  }, [fetchAllFunders]);

  // Keep original function name for compatibility
  const fetchFunders = fetchAllFunders;

  const fetchCurrentUserAndFunders = useCallback(async () => {
    try {
      setLoading(true);
      // First get current user
      const userResponse = await axios.get('/api/accounts/user/');
      console.log('Current user:', userResponse.data);
      setCurrentUserId(userResponse.data.id);
      
      // Then fetch user's assigned funds
      if (userResponse.data.id) {
        await fetchUserAssignedFunders(userResponse.data.id);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      // Fallback to all funders if user ID fetch fails
      await fetchAllFunders();
    }
  }, [fetchUserAssignedFunders, fetchAllFunders]);

  useEffect(() => {
    fetchCurrentUserAndFunders();
  }, [fetchCurrentUserAndFunders]);

  const handleViewFunder = async (funder) => {
    console.log('Opening funder view modal for:', funder.name);
    console.log('User permissions:');
    console.log('- Manage Representatives (unified):', canManageFunderRepresentatives());
    console.log('- View Representatives (unified):', canViewFunderRepresentatives());
    console.log('- Manage Officers:', canManageFunderOfficers());
    console.log('- View Officers:', canViewFunderOfficers());
    
    // Debug individual permissions grouped by type
    console.log('Representative permissions breakdown:');
    console.log('  Primary: add=', hasAnyPermission(['add_funderprimaryrepresentative']), 
                'change=', hasAnyPermission(['change_funderprimaryrepresentative']),
                'view=', hasAnyPermission(['view_funderprimaryrepresentative']));
    console.log('  Secondary: add=', hasAnyPermission(['add_fundersecondaryrepresentative']), 
                'change=', hasAnyPermission(['change_fundersecondaryrepresentative']),
                'view=', hasAnyPermission(['view_fundersecondaryrepresentative']));
    
    setSelectedFunder(funder);
    setActiveTab('details');
    setViewModal(true);
    
    // Fetch representatives data for this funder
    await fetchFunderRepresentatives(funder.id);
  };

  const fetchFunderRepresentatives = async (funderId) => {
    try {
      setRepresentativesLoading(true);
      const response = await axios.get(`accounts/funders/${funderId}/representatives/`);
      setRepresentatives(response.data);
    } catch (error) {
      console.error('Error fetching representatives:', error);
      setRepresentatives({
        primary_representatives: [],
        secondary_representatives: [],
        officers: []
      });
    } finally {
      setRepresentativesLoading(false);
    }
  };

  const handleMaintainFunder = async (funder) => {
    console.log('Opening funder maintenance modal for:', funder.name);
    console.log('User permissions - Manage Funders:', canManageFunders());
    
    setSelectedFunder(funder);
    setEditedFunder({ ...funder }); // Create a copy for editing
    setIsEditMode(true);
    setActiveTab('details');
    setViewModal(true);
    setHasChanges(false);
    
    // Fetch representatives data for this funder
    await fetchFunderRepresentatives(funder.id);
  };

  const handleFieldChange = (fieldName, value) => {
    setEditedFunder(prev => ({
      ...prev,
      [fieldName]: value
    }));
    setHasChanges(true);
  };

  const handleSaveFunder = async () => {
    if (!editedFunder || !hasChanges) return;
    
    // Basic validation
    if (!editedFunder.name || !editedFunder.name.trim()) {
      setError('Funder name is required');
      return;
    }
    
    try {
      setIsSaving(true);
      setError(''); // Clear any previous errors
      console.log('Saving funder changes:', editedFunder);
      
      // API call to update funder
      const response = await axios.put(`/api/accounts/funders/${editedFunder.id}/`, editedFunder);
      
      if (response.status === 200) {
        // Update the selectedFunder with the saved data
        setSelectedFunder(response.data);
        setEditedFunder(response.data);
        setHasChanges(false);
        setIsEditMode(false);
        
        // Refresh the funders list to show updated data
        await fetchFunders();
        
        console.log('Funder updated successfully');
        // You could add a success toast/notification here
      }
    } catch (error) {
      console.error('Error saving funder:', error);
      if (error.response?.data?.detail) {
        setError(`Failed to save: ${error.response.data.detail}`);
      } else if (error.response?.data) {
        // Handle field-specific errors
        const errorMessages = Object.entries(error.response.data)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
          .join('; ');
        setError(`Validation errors: ${errorMessages}`);
      } else {
        setError('Failed to save funder changes. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    if (selectedFunder) {
      setEditedFunder({ ...selectedFunder });
      setHasChanges(false);
      setIsEditMode(false);
    }
  };

  const handleFunderOffices = (funder) => {
    // Navigate to Add Funder Officer page in general dashboard
    navigate(`/general-dashboard/funders/add-officer/${funder.id}`);
  };

  const closeViewModal = () => {
    // If in edit mode with unsaved changes, show confirmation
    if (isEditMode && hasChanges) {
      const confirmClose = window.confirm(
        'You have unsaved changes. Are you sure you want to close without saving?'
      );
      if (!confirmClose) {
        return; // Don't close if user cancels
      }
    }
    
    setViewModal(false);
    setSelectedFunder(null);
    setEditedFunder(null);
    setIsEditMode(false);
    setHasChanges(false);
    setActiveTab('details');
    setRepresentatives({
      primary_representatives: [],
      secondary_representatives: [],
      officers: []
    });
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner color="primary" />
        <p className="mt-2">Loading Funders...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Funders</h2>
        {canCreateFunders() && (
          <Button 
            color="primary" 
            onClick={() => navigate('/general-dashboard/funders/create')}
            className="d-flex align-items-center"
          >
            <i className="fas fa-plus me-2"></i>
            ADD +
          </Button>
        )}
      </div>
      
      {error && (
        <Alert color="danger" className="mb-3">
          {error}
        </Alert>
      )}
      
      <Card style={{ minHeight: '500px' }}>
        <CardBody style={{ minHeight: '450px' }}>
          <CardTitle tag="h5">
            <Badge color="primary" className="ms-2">
              {funders.length} Total
            </Badge>
          </CardTitle>
          
          {funders.length === 0 ? (
            <Alert color="info">
              No funders found. Create your first funder to get started.
            </Alert>
          ) : (
            <div className="table-responsive" style={{ minHeight: '500px' }}>
              <Table striped hover style={{ minHeight: '450px' }}>
                <thead>
                  <tr>
                    <th>Funder Code</th>
                    <th>Funder Name</th>
                    <th>Short Name</th>
                    <th>Country</th>
                    <th>Domain</th>
                    <th>Currency</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {funders.map((funder) => (
                    <tr key={funder.id}>
                      <td>
                        <strong className="text-primary">{funder.funder_code || '-'}</strong>
                      </td>
                      <td>
                        <strong>{funder.name}</strong>
                      </td>
                      <td>
                        {funder.short_name || '-'}
                      </td>
                      <td>
                        {funder.country || '-'}
                      </td>
                      <td>
                        {funder.domain || '-'}
                      </td>
                      <td>
                        <Badge color="info" pill>
                          {funder.default_currency || 'ZAR'}
                        </Badge>
                      </td>
                      <td>
                        <UncontrolledDropdown>
                          <DropdownToggle 
                            size="sm" 
                            color="outline-primary"
                          >
                            ⋯
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem 
                              onClick={() => handleViewFunder(funder)}
                            >
                              <i className="fas fa-eye me-2"></i>
                              View
                            </DropdownItem>
                            {hasAnyPermission(['change_funder']) && (
                              <DropdownItem 
                                onClick={() => handleMaintainFunder(funder)}
                              >
                                <i className="fas fa-edit me-2"></i>
                                Maintain
                              </DropdownItem>
                            )}
                            {hasAnyPermission(['add_funder']) && (
                              <DropdownItem 
                                onClick={() => handleFunderOffices(funder)}
                              >
                                <i className="fas fa-building me-2"></i>
                                Funder Officers
                              </DropdownItem>
                            )}
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Funder Details Modal with Tabs */}
      <Modal isOpen={viewModal} toggle={closeViewModal} size="xl" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
        <ModalHeader toggle={closeViewModal} style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
          <div className="d-flex justify-content-center align-items-center w-100">
            {/* BMO Information Table in Header */}
            <Table bordered size="sm" className="mb-0" style={{ maxWidth: '600px' }}>
              <tbody>
                <tr>
                  <td style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold', width: '20%', fontSize: '0.9rem' }}>
                    BMO
                  </td>
                  <td style={{ width: '30%', fontSize: '0.9rem' }}>
                    {selectedFunder?.bmo?.code || 'Not set'}
                  </td>
                  <td style={{ fontSize: '0.9rem' }}>
                    {selectedFunder?.bmo?.name || 'Not available'}
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </ModalHeader>
        <ModalBody style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
          {selectedFunder && (
            <>
              {/* Tab Navigation */}
              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={activeTab === 'details' ? 'active' : ''}
                    onClick={() => setActiveTab('details')}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="fas fa-info-circle me-2"></i>
                    Funder Details
                  </NavLink>
                </NavItem>
                {canViewFunderRepresentatives() && (
                  <NavItem>
                    <NavLink
                      className={activeTab === 'representatives' ? 'active' : ''}
                      onClick={() => setActiveTab('representatives')}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="fas fa-users me-2"></i>
                      Representatives
                    </NavLink>
                  </NavItem>
                )}
                {canViewFunderOfficers() && (
                  <NavItem>
                    <NavLink
                      className={activeTab === 'officers' ? 'active' : ''}
                      onClick={() => setActiveTab('officers')}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="fas fa-user-tie me-2"></i>
                      Officers
                    </NavLink>
                  </NavItem>
                )}
              </Nav>

              {/* Error Alert */}
              {error && (
                <Alert color="danger" className="mt-3">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </Alert>
              )}

              {/* Tab Content */}
              <TabContent activeTab={activeTab} className="mt-3">
                {/* Funder Details Tab */}
                <TabPane tabId="details">
                  {isEditMode ? (
                    /* Edit Mode - Editable Fields */
                    <>
                      <Row className="mb-3">
                        <Col md={6}>
                          <FormGroup>
                            <Label for="country"><strong>Funder Nationality:</strong></Label>
                            <Input
                              type="text"
                              id="country"
                              value={editedFunder?.country || ''}
                              onChange={(e) => handleFieldChange('country', e.target.value)}
                              placeholder="Enter funder nationality"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="name"><strong>Funder Name:</strong></Label>
                            <Input
                              type="text"
                              id="name"
                              value={editedFunder?.name || ''}
                              onChange={(e) => handleFieldChange('name', e.target.value)}
                              placeholder="Enter funder name"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={6}>
                          <FormGroup>
                            <Label for="code"><strong>System Funder Code:</strong></Label>
                            <Input
                              type="text"
                              id="code"
                              value={editedFunder?.code || ''}
                              onChange={(e) => handleFieldChange('code', e.target.value)}
                              placeholder="Enter system funder code"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="short_name"><strong>Funder Short Name:</strong></Label>
                            <Input
                              type="text"
                              id="short_name"
                              value={editedFunder?.short_name || ''}
                              onChange={(e) => handleFieldChange('short_name', e.target.value)}
                              placeholder="Enter funder short name"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={6}>
                          <FormGroup>
                            <Label for="funder_code"><strong>BMO Funder Code:</strong></Label>
                            <Input
                              type="text"
                              id="funder_code"
                              value={editedFunder?.funder_code || ''}
                              onChange={(e) => handleFieldChange('funder_code', e.target.value)}
                              placeholder="Enter BMO funder code"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="domain"><strong>Funder Domain:</strong></Label>
                            <Input
                              type="text"
                              id="domain"
                              value={editedFunder?.domain || ''}
                              onChange={(e) => handleFieldChange('domain', e.target.value)}
                              placeholder="Enter funder domain"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={6}>
                          <FormGroup>
                            <Label><strong>Funder Website:</strong></Label>
                            <div className="form-control-plaintext text-muted">
                              {editedFunder?.domain ? `www.${editedFunder.domain}` : 'No domain specified'}
                            </div>
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="default_currency"><strong>Reporting Currency:</strong></Label>
                            <Input
                              type="select"
                              id="default_currency"
                              value={editedFunder?.default_currency || 'ZAR'}
                              onChange={(e) => handleFieldChange('default_currency', e.target.value)}
                            >
                              <option value="">Select Currency</option>
                              {CURRENCIES.map((currency) => (
                                <option key={currency.code} value={currency.code}>
                                  {currency.code} - {currency.name}
                                </option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>

                      <hr />
                      <Row className="mb-3">
                        <Col md={6}>
                          <FormGroup>
                            <Label><strong>Created Date:</strong></Label>
                            <div className="text-muted">
                              {selectedFunder.created_at ? new Date(selectedFunder.created_at).toLocaleDateString() : 'Not available'}
                            </div>
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label><strong>Created By:</strong></Label>
                            <div className="text-muted">
                              {selectedFunder.created_by_name || 'Not available'}
                            </div>
                          </FormGroup>
                        </Col>
                      </Row>
                    </>
                  ) : (
                    /* View Mode - Read-only Fields */
                    <>
                      <Row className="mb-3">
                        <Col md={6}>
                          <strong>Funder Nationality:</strong>
                          <div className="text-muted">{selectedFunder.country || 'Not specified'}</div>
                        </Col>
                        <Col md={6}>
                          <strong>Funder Name:</strong>
                          <div className="text-muted">{selectedFunder.name}</div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={6}>
                          <strong>System Funder Code:</strong>
                          <div className="text-muted">{selectedFunder.code || 'Not assigned'}</div>
                        </Col>
                        <Col md={6}>
                          <strong>Funder Short Name:</strong>
                          <div className="text-muted">{selectedFunder.short_name || 'Not specified'}</div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={6}>
                          <strong>BMO Funder Code:</strong>
                          <div className="text-muted">{selectedFunder.funder_code || 'Not assigned'}</div>
                        </Col>
                        <Col md={6}>
                          <strong>Funder Domain:</strong>
                          <div className="text-muted">{selectedFunder.domain || 'Not specified'}</div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={6}>
                          <strong>Funder Website:</strong>
                          <div className="text-muted">
                            {selectedFunder.domain ? (
                              <a href={`https://www.${selectedFunder.domain}`} target="_blank" rel="noopener noreferrer" className="text-primary">
                                www.{selectedFunder.domain}
                              </a>
                            ) : (
                              'No domain specified'
                            )}
                          </div>
                        </Col>
                        <Col md={6}>
                          <strong>Reporting Currency:</strong>
                          <div className="text-muted">
                            <Badge color="info" pill>
                              {selectedFunder.default_currency || 'ZAR'}
                            </Badge>
                          </div>
                        </Col>
                      </Row>

                      <hr />
                      <Row className="mb-3">
                        <Col md={6}>
                          <strong>Created Date:</strong>
                          <div className="text-muted">
                            {selectedFunder.created_at ? new Date(selectedFunder.created_at).toLocaleDateString() : 'Not available'}
                          </div>
                        </Col>
                        <Col md={6}>
                          <strong>Created By:</strong>
                          <div className="text-muted">
                            {selectedFunder.created_by_name || 'Not available'}
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}
                </TabPane>

                {/* Representatives Tab */}
                {canViewFunderRepresentatives() && (
                  <TabPane tabId="representatives">
                  {representativesLoading ? (
                    <div className="text-center p-4">
                      <Spinner color="primary" size="sm" />
                      <p className="mt-2">Loading representatives...</p>
                    </div>
                  ) : (
                    <>
                      {/* Primary Representatives */}
                      <h6 className="text-primary mb-3">
                        <i className="fas fa-star me-2"></i>
                        Primary Representatives
                      </h6>
                      {representatives.primary_representatives.length > 0 ? (
                        <div className="mb-4">
                          {representatives.primary_representatives.map((rep, index) => (
                            <Card key={index} className="mb-2">
                              <CardBody className="p-3">
                                <Row>
                                  <Col md={6}>
                                    <strong>Name:</strong>
                                    <div className="text-muted">{rep.first_name} {rep.last_name}</div>
                                  </Col>
                                  <Col md={6}>
                                    <strong>Email:</strong>
                                    <div className="text-muted">{rep.email_address}</div>
                                  </Col>
                                </Row>
                                <Row className="mt-2">
                                  <Col md={6}>
                                    <strong>Title:</strong>
                                    <div className="text-muted">{rep.title || 'Not specified'}</div>
                                  </Col>
                                  <Col md={6}>
                                    <strong>Phone:</strong>
                                    <div className="text-muted">{rep.phone_number || 'Not specified'}</div>
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <Alert color="info" className="mb-4">
                          No primary representatives found for this funder.
                        </Alert>
                      )}

                      {/* Secondary Representatives */}
                      <h6 className="text-primary mb-3">
                        <i className="fas fa-user-friends me-2"></i>
                        Secondary Representatives
                      </h6>
                      {representatives.secondary_representatives.length > 0 ? (
                        <div className="mb-4">
                          {representatives.secondary_representatives.map((rep, index) => (
                            <Card key={index} className="mb-2">
                              <CardBody className="p-3">
                                <Row>
                                  <Col md={6}>
                                    <strong>Name:</strong>
                                    <div className="text-muted">{rep.first_name} {rep.last_name}</div>
                                  </Col>
                                  <Col md={6}>
                                    <strong>Email:</strong>
                                    <div className="text-muted">{rep.email_address}</div>
                                  </Col>
                                </Row>
                                <Row className="mt-2">
                                  <Col md={6}>
                                    <strong>Title:</strong>
                                    <div className="text-muted">{rep.title || 'Not specified'}</div>
                                  </Col>
                                  <Col md={6}>
                                    <strong>Phone:</strong>
                                    <div className="text-muted">{rep.phone_number || 'Not specified'}</div>
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <Alert color="info" className="mb-4">
                          No secondary representatives found for this funder.
                        </Alert>
                      )}
                    </>
                  )}
                </TabPane>
                )}

                {/* Officers Tab */}
                {canViewFunderOfficers() && (
                  <TabPane tabId="officers">
                  {representativesLoading ? (
                    <div className="text-center p-4">
                      <Spinner color="primary" size="sm" />
                      <p className="mt-2">Loading officers...</p>
                    </div>
                  ) : (
                    <>
                      <h6 className="text-primary mb-3">
                        <i className="fas fa-user-tie me-2"></i>
                        Funder Officers
                      </h6>
                      {representatives.officers.length > 0 ? (
                        <div className="mb-4">
                          {representatives.officers.map((officer, index) => (
                            <Card key={index} className="mb-2">
                              <CardBody className="p-3">
                                <Row>
                                  <Col md={6}>
                                    <strong>Name:</strong>
                                    <div className="text-muted">{officer.first_name} {officer.last_name}</div>
                                  </Col>
                                  <Col md={6}>
                                    <strong>Email:</strong>
                                    <div className="text-muted">{officer.email_address}</div>
                                  </Col>
                                </Row>
                                <Row className="mt-2">
                                  <Col md={6}>
                                    <strong>Title:</strong>
                                    <div className="text-muted">{officer.title || 'Not specified'}</div>
                                  </Col>
                                  <Col md={6}>
                                    <strong>Phone:</strong>
                                    <div className="text-muted">{officer.phone_number || 'Not specified'}</div>
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <Alert color="info" className="mb-4">
                          No officers found for this funder.
                        </Alert>
                      )}
                    </>
                  )}
                </TabPane>
                )}
              </TabContent>
            </>
          )}
        </ModalBody>
        <ModalFooter style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
          {isEditMode ? (
            /* Edit Mode Buttons */
            <>
              <Button 
                color="success" 
                onClick={handleSaveFunder}
                disabled={!hasChanges || isSaving}
              >
                {isSaving ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    Save Changes
                  </>
                )}
              </Button>
              <Button 
                color="warning" 
                onClick={handleDiscardChanges}
                disabled={isSaving}
              >
                <i className="fas fa-times me-2"></i>
                Discard Changes
              </Button>
              <Button 
                color="secondary" 
                onClick={closeViewModal}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </>
          ) : (
            /* View Mode Button */
            <Button color="secondary" onClick={closeViewModal}>
              Close
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ManageFunders;
